/**
 * Orb ajanı — tarayıcı tarafı (katalog s. 25–28).
 *
 * Model Worker /api/agent üzerinden araç çağırır; araçlar BURADA, kullanıcının
 * kendi oturumuyla çalışır (RLS). Sayıyı kod bulur, model yalnızca söyler.
 *
 *   · Okuyan araçlar: sayfa aç, özet, kayıt bul, bekleyen talepler, müşteri
 *     listesi, müşteri aç, eksikler, belge, son iş, kaldığın yerden devam.
 *   · Dışarı giden araçlar (belge talebi, bağlantı kodu) yalnızca ONAY KARTI
 *     çizer; gönderim kullanıcı "Send" dediğinde olur.
 *   · Belge açma: tarayıcı kendiliğinden açılan pencereyi engeller; sohbete
 *     "Open" düğmesi konur, açma kullanıcının tıklamasıyla (izli) olur.
 *   · Her müşteri/belge/talep işi log_activity ile iş günlüğüne yazılır.
 *   · Silme aracı yok.
 *
 * Ajan kapalıysa (AGENT_OFF) ya da hata olursa null döner: sohbet düz AI'a döner.
 *
 * AI sayfasında (katalog s. 27) cevap bir iz taşır: çalışan araçlar
 * ("⚙ open_client(…)"), birden çok eşleşmede seçim düğmeleri, açılacak sayfa.
 * Sayfa AI sayfasındayken kendiliğinden DEĞİŞMEZ: sohbet ve onay kartları bu
 * sayfada; kullanıcı götüren düğmeye basar. Başka yerden çağrılırsa gider.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import { KIND_LABEL } from '../doc-requests/texts.ts';

type Side = 'customer' | 'firm';
interface ToolCall { id: string; function: { name: string; arguments: string } }
interface AgentMsg { role: string; content: string; tool_calls?: ToolCall[]; tool_call_id?: string }
interface Trace { tools: string[]; choices: Array<{ label: string; value: string }>; page: { id: string; label: string } | null }
interface ChatShape { choices: Array<{ message: { content: string } }>; _agent?: Trace }

const g = globalThis as any;
const pending = new Map<string, () => Promise<string>>();
let overviewCache: { at: number; data: any } | null = null;
let trace: Trace = { tools: [], choices: [], page: null };
const pageGo = new Map<string, () => void>();
const TAB_LABEL: Record<string, string> = { inbox: 'Inbox', missing: 'Missing', entries: 'Entries', close: 'Month close' };

function onAiPage(): boolean { return !!document.getElementById('v6')?.classList.contains('on'); }

/** AI sayfasında sayfayı hemen açmaz, sohbete götüren düğme koyar. */
function offerPage(label: string, go: () => void): boolean {
  if (!onAiPage()) { go(); return false; }
  const id = 'pg-' + Math.random().toString(36).slice(2, 10);
  pageGo.set(id, go);
  trace.page = { id, label };
  return true;
}

function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
function sb(): any { return legacy.sb; }
function side(): Side { const s = g.navSide?.(); return s && s !== 'customer' ? 'firm' : 'customer'; }

async function rpc(fn: string, args: Record<string, unknown> = {}): Promise<any> {
  try { const r = await sb().rpc(fn, args); return r.error ? null : r.data; } catch { return null; }
}
async function log(kind: string, linkId: string | null, documentId: string | null, label: string, position: Record<string, unknown> = {}): Promise<void> {
  await rpc('log_activity', { p_kind: kind, p_link_id: linkId, p_document_id: documentId, p_label: label, p_position: position, p_source: 'agent' });
}

/* ── Müşteri araçları ──────────────────────────────────────────────── */

function periodFilter(period: string): (d: string) => boolean {
  const now = new Date();
  const ym = (y: number, m: number) => `${y}-${String(m + 1).padStart(2, '0')}`;
  if (period === 'this_month') { const p = ym(now.getFullYear(), now.getMonth()); return (d) => d.startsWith(p); }
  if (period === 'last_month') { const x = new Date(now.getFullYear(), now.getMonth() - 1, 1); const p = ym(x.getFullYear(), x.getMonth()); return (d) => d.startsWith(p); }
  if (period === 'this_year') { const p = String(now.getFullYear()); return (d) => d.startsWith(p); }
  return () => true;
}

function ledger(): Array<{ date: string; type: string; amount: number; cat: string; note?: string; cur: string }> {
  return (legacy.user?.exp ?? []) as never;
}

function ledgerSummary(args: { period?: string }) {
  const f = periodFilter(args.period ?? 'this_month');
  let income = 0, expense = 0; const cats: Record<string, number> = {};
  const rows = ledger().filter((e) => f(String(e.date || '')));
  for (const e of rows) {
    const a = Number(e.amount) || 0;
    if (e.type === 'income') income += a; else { expense += a; cats[e.cat || 'Other'] = (cats[e.cat || 'Other'] || 0) + a; }
  }
  const top = Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([category, amount]) => ({ category, amount: Math.round(amount * 100) / 100 }));
  const currency = (legacy.user?.setup as { currency?: string } | undefined)?.currency ?? '';
  return { ok: true, period: args.period ?? 'this_month', currency, entries: rows.length,
    income: Math.round(income * 100) / 100, expense: Math.round(expense * 100) / 100, net: Math.round((income - expense) * 100) / 100, top_expense_categories: top };
}

function findEntries(args: { query?: string; period?: string }) {
  const q = String(args.query ?? '').trim().toLowerCase();
  if (!q) return { ok: false, error: 'Tell me what to look for.' };
  const f = periodFilter(args.period ?? 'all');
  const rows = ledger().filter((e) => f(String(e.date || '')) && `${e.note ?? ''} ${e.cat ?? ''}`.toLowerCase().includes(q))
    .slice(0, 10).map((e) => ({ date: e.date, type: e.type, amount: e.amount, currency: e.cur, category: e.cat, description: e.note ?? '' }));
  return { ok: true, count: rows.length, entries: rows };
}

async function pendingRequests() {
  const r = await sb().from('document_requests')
    .select('title,due_date,document_request_items(kind,label,status)').eq('status', 'open').limit(10);
  const rows = (r && r.data) || [];
  return { ok: true, requests: rows.map((x: any) => ({ title: x.title, due_date: x.due_date,
    missing: (x.document_request_items || []).filter((i: any) => i.status === 'missing').map((i: any) => (KIND_LABEL[i.kind] ?? i.kind) + (i.label ? ' — ' + i.label : '')) })) };
}

/* ── Ofis araçları ─────────────────────────────────────────────────── */

async function overview(): Promise<any> {
  if (overviewCache && Date.now() - overviewCache.at < 60_000) return overviewCache.data;
  const d = await rpc('firm_clients_overview');
  overviewCache = { at: Date.now(), data: d };
  return d;
}

/** İsimle müşteri bul: tam eşleşme > başlangıç > içerir. Birden çoksa sorar. */
async function resolveClient(name: string): Promise<{ ok: true; client: any } | { ok: false; error: string; matches?: string[] }> {
  const o = await overview();
  const list: any[] = (o && o.clients) || [];
  const q = String(name ?? '').trim().toLowerCase();
  if (!q) return { ok: false, error: 'Which client?' };
  const by = (pred: (c: any) => boolean) => list.filter(pred);
  let hits = by((c) => c.client_name.toLowerCase() === q || c.contact_name.toLowerCase() === q);
  if (!hits.length) hits = by((c) => c.client_name.toLowerCase().startsWith(q));
  if (!hits.length) hits = by((c) => c.client_name.toLowerCase().includes(q) || c.contact_name.toLowerCase().includes(q));
  if (hits.length === 1) return { ok: true, client: hits[0] };
  if (!hits.length) return { ok: false, error: `No client named "${name}" that you can open.` };
  const some = hits.slice(0, 6);
  trace.choices = some.map((c) => ({ label: `${c.contact_name} · ${c.client_name}`, value: c.client_name }));
  return { ok: false, error: 'Several clients match. Ask which one; buttons with the names are shown in the chat.', matches: some.map((c) => c.client_name) };
}

function card(html: string): void {
  if (typeof g.aiAddMsg === 'function') g.aiAddMsg('bot', html);
}

async function openClient(args: { client?: string; tab?: string }) {
  const r = await resolveClient(args.client ?? '');
  if (!r.ok) return r;
  const tab = ['inbox', 'missing', 'entries', 'close'].includes(String(args.tab)) ? String(args.tab) : 'inbox';
  const offered = offerPage(`${r.client.client_name} › ${TAB_LABEL[tab]}`, () => g.fwNav?.('client', r.client.link_id, tab));
  await log('open_client', r.client.link_id, null, r.client.client_name, { tab });
  return { ok: true, client: r.client.client_name, tab, note: offered ? 'A button that opens this page is shown in the chat.' : 'Page opened.' };
}

async function clientMissing(args: { client?: string }) {
  const r = await resolveClient(args.client ?? '');
  if (!r.ok) return r;
  const d = await rpc('firm_client_detail', { p_link_id: r.client.link_id });
  if (!d || !d.ok) return { ok: false, error: 'Client not found.' };
  const missing = (d.requests || []).filter((q: any) => q.status === 'open').flatMap((q: any) =>
    q.items.filter((i: any) => i.status === 'missing').map((i: any) => ({ request: q.title, document: (KIND_LABEL[i.kind] ?? i.kind) + (i.label ? ' — ' + i.label : ''), due_date: q.due_date, reminders_sent: q.reminder_count })));
  return { ok: true, client: r.client.client_name, missing };
}

async function openDocument(args: { client?: string; document?: string }) {
  const r = await resolveClient(args.client ?? '');
  if (!r.ok) return r;
  const d = await rpc('firm_client_detail', { p_link_id: r.client.link_id });
  const q = String(args.document ?? '').trim().toLowerCase();
  const docs = ((d && d.documents) || []).filter((x: any) => x.name.toLowerCase().includes(q) && x.has_file);
  if (!docs.length) return { ok: false, error: `No document matching "${args.document}" for ${r.client.client_name}.` };
  const doc = docs[0];
  /* Belge penceresi yalnız kullanıcının tıklamasıyla açılır (tarayıcı engeli + erişim izi). */
  const id = 'pg-' + Math.random().toString(36).slice(2, 10);
  pageGo.set(id, () => { g.fwNav?.('client', r.client.link_id, 'inbox'); g.faOpenDocument?.(doc.id); });
  trace.page = { id, label: `${r.client.client_name} › Inbox › ${doc.name}` };
  await log('open_document', r.client.link_id, doc.id, doc.name, { client: r.client.client_name });
  return { ok: true, document: doc.name, client: r.client.client_name, note: 'An Open button is shown in the chat. Opening is recorded.' };
}

async function requestDocuments(args: { client?: string; items?: Array<{ kind?: string; label?: string }>; due_date?: string; email?: boolean }) {
  const r = await resolveClient(args.client ?? '');
  if (!r.ok) return r;
  const items = (args.items ?? []).filter((i) => i && KIND_LABEL[String(i.kind)]).slice(0, 20)
    .map((i) => ({ kind: String(i.kind), label: String(i.label ?? '').slice(0, 80) }));
  if (!items.length) return { ok: false, error: 'Which documents should be requested?' };
  const due = /^\d{4}-\d{2}-\d{2}$/.test(String(args.due_date ?? '')) ? String(args.due_date) : null;
  const email = args.email !== false;
  const id = 'ag-' + Math.random().toString(36).slice(2, 10);
  const title = new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) + ' documents';
  pending.set(id, async () => {
    const res = await rpc('create_document_request', { p_link_id: r.client.link_id, p_title: title, p_message: '', p_period: '',
      p_due_date: due, p_items: items, p_channels: email ? ['in_app', 'email'] : ['in_app'] });
    if (!res || !res.ok) return `<span style="color:var(--red)">${esc(res?.error ?? 'The request could not be sent.')}</span>`;
    let note = 'Sent in the app.';
    if (email && res.request_id) {
      try { const x = await legacy.postApi('/api/requests/notify', { request_id: res.request_id }); note = x.ok ? 'Sent by email and in the app.' : 'Sent in the app. The email could not be sent.'; }
      catch { note = 'Sent in the app. The email could not be sent.'; }
    }
    await log('request_documents', r.client.link_id, null, title, { items: items.length });
    overviewCache = null;
    return `<span style="color:var(--grn)">✓ ${esc(note)}</span>`;
  });
  const dueLabel = due ? new Date(`${due}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';
  const editId = 'pg-' + Math.random().toString(36).slice(2, 10);
  pageGo.set(editId, () => { g.fwNav?.('client', r.client.link_id, 'missing'); g.fwForm?.(true); });
  card(`<div id="${id}" class="ag-confirm"><div class="ag-confirm-h">✋ Confirm before sending</div>`
    + `<div>Send a document request to <b>${esc(r.client.contact_name)} (${esc(r.client.client_name)})</b></div>`
    + items.map((i) => `<div class="ag-line">• ${esc(KIND_LABEL[i.kind])}${i.label ? ' — ' + esc(i.label) : ''}${dueLabel ? ' · due <span translate="no">' + esc(dueLabel) + '</span>' : ''}</div>`).join('')
    + `<div class="ag-line">• ${email ? 'By email and in app' : 'In app'} · reminders on day 3 and 7</div>`
    + `<div data-actions class="ag-actions"><button class="btn bg bsm" onclick="agentGo('${editId}')">Edit</button>`
    + `<button class="btn bg bsm" onclick="agentCancel('${id}')">Cancel</button><span style="flex:1"></span>`
    + `<button class="btn br bsm" onclick="agentConfirm('${id}')">Send request</button></div></div>`);
  return { ok: true, status: 'awaiting_confirmation', client: r.client.client_name, items: items.length };
}

function linkClientCode(args: { label?: string }) {
  const label = String(args.label ?? '').slice(0, 80);
  const id = 'ag-' + Math.random().toString(36).slice(2, 10);
  pending.set(id, async () => {
    const res = await rpc('create_firm_invite', { p_label: label });
    if (!res || !res.ok) return `<span style="color:var(--red)">${esc(res?.error ?? 'The code could not be created.')}</span>`;
    await log('link_client_code', null, null, label || 'client');
    return `Code <b translate="no" style="font-family:monospace;color:#D4A017">${esc(res.code)}</b> · valid 10 minutes, single use. Give it to your client.`;
  });
  card(`<div id="${id}" class="ag-confirm"><div class="ag-confirm-h">✋ Confirm before creating</div>`
    + `<div>Create a link code${label ? ' for <b>' + esc(label) + '</b>' : ''}</div>`
    + `<div class="ag-line">• One-time code · valid 10 minutes</div>`
    + `<div class="ag-line">• The client confirms the link in their own account</div>`
    + `<div data-actions class="ag-actions"><button class="btn bg bsm" onclick="agentCancel('${id}')">Cancel</button><span style="flex:1"></span>`
    + `<button class="btn br bsm" onclick="agentConfirm('${id}')">Create code</button></div></div>`);
  return { ok: true, status: 'awaiting_confirmation' };
}

/** "2023 dosyasında 15000. satır" (katalog s. 29): satırı uygulama bulur, modele yalnız o satır gider. */
export function pickRow(matrix: unknown[][], row: number): { ok: true; cells: Record<string, string> } | { ok: false; error: string; rows?: number } {
  if (!Number.isInteger(row) || row < 1) return { ok: false, error: 'Row numbers start at 1.' };
  if (row > matrix.length) return { ok: false, error: `The file has ${matrix.length} rows.`, rows: matrix.length };
  const header = (matrix[0] ?? []).map((h, i) => String(h ?? '').trim() || `Column ${i + 1}`);
  const values = matrix[row - 1] ?? [];
  const cells: Record<string, string> = {};
  const width = Math.min(Math.max(header.length, values.length), 40);
  for (let i = 0; i < width; i++) {
    const v = values[i];
    if (v === null || v === undefined || String(v).trim() === '') continue;
    cells[row === 1 ? `Column ${i + 1}` : (header[i] ?? `Column ${i + 1}`)] = String(v).slice(0, 200);
  }
  return { ok: true, cells };
}

async function getRow(args: { client?: string; document?: string; row?: number }) {
  const r = await resolveClient(args.client ?? '');
  if (!r.ok) return r;
  const d = await rpc('firm_client_detail', { p_link_id: r.client.link_id });
  const q = String(args.document ?? '').trim().toLowerCase();
  const docs = ((d && d.documents) || []).filter((x: any) => x.has_file && /^(xlsx|xls|csv|ods|txt)$/.test(String(x.file_type)) && x.name.toLowerCase().includes(q));
  if (!docs.length) return { ok: false, error: `No spreadsheet matching "${args.document}" for ${r.client.client_name}.` };
  if (docs.length > 1) {
    trace.choices = docs.slice(0, 6).map((x: any) => ({ label: x.name, value: `Row ${args.row} of ${x.name} for ${r.client.client_name}` }));
    return { ok: false, error: 'Several files match. Ask which one; buttons with the file names are shown.', files: docs.slice(0, 6).map((x: any) => x.name) };
  }
  const doc = docs[0];
  const X = g.XLSX;
  if (!X) return { ok: false, error: 'The spreadsheet reader is not loaded.' };
  let matrix: unknown[][];
  try {
    const res = await legacy.postApi('/api/files/open', { document_id: doc.id, action: 'download' });
    const data = await res.json().catch(() => ({})) as { url?: string; error?: string };
    if (!res.ok || !data.url) return { ok: false, error: data.error || 'The file could not be opened.' };
    const buf = await (await fetch(data.url)).arrayBuffer();
    /* CSV metin olarak kalır (tarih seri sayıya dönmesin); tablolarda hücre ekranda göründüğü gibi okunur. */
    const wb = X.read(buf, { type: 'array', dense: true, raw: /^(csv|txt)$/.test(String(doc.file_type)) });
    matrix = X.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, blankrows: true, defval: '', raw: false });
  } catch {
    return { ok: false, error: 'The file could not be read.' };
  }
  const row = Number(args.row);
  const picked = pickRow(matrix, row);
  const id = 'pg-' + Math.random().toString(36).slice(2, 10);
  pageGo.set(id, () => { g.fwNav?.('client', r.client.link_id, 'inbox'); g.faOpenDocument?.(doc.id); });
  trace.page = { id, label: `${r.client.client_name} › ${doc.name} › row ${row}` };
  /* Günlük türleri veritabanında sabit; satır okuma bir belge açılışı olarak yazılır (satır numarasıyla). */
  await log('open_document', r.client.link_id, doc.id, doc.name, { client: r.client.client_name, row });
  return picked.ok ? { ok: true, client: r.client.client_name, document: doc.name, row, cells: picked.cells } : picked;
}

/* ── Ortak araçlar ─────────────────────────────────────────────────── */

const PAGES: Record<string, () => void> = {
  ai: () => legacy.navigate('v6', 'ni6'), overview: () => legacy.navigate('v0', 'ni0'),
  financial_results: () => legacy.navigate('v1', 'ni1'), proposed_solutions: () => legacy.navigate('v2', 'ni2'),
  my_accountant: () => legacy.navigate('v10', 'ni10'), membership: () => legacy.navigate('v7', 'ni7'),
  settings: () => legacy.navigate('v4', 'ni4'), global: () => legacy.navigate('v3', 'ni3'),
  documents: () => g.aipOpenDoc?.(), clients: () => g.fwNav?.('clients'), inbox: () => g.fwNav?.('inbox'),
  missing_documents: () => g.fwNav?.('missing'), month_close: () => g.fwNav?.('close'), team: () => legacy.navigate('v11', 'ni11'),
};

async function openPage(args: { page?: string }) {
  const go = PAGES[String(args.page)];
  if (!go) return { ok: false, error: 'Unknown page.' };
  go();
  await log('open_page', null, null, String(args.page));
  return { ok: true, opened: args.page };
}

async function recentActivity() {
  const r = await sb().from('activity_log').select('kind,label,created_at').order('created_at', { ascending: false }).limit(10);
  return { ok: true, activity: ((r && r.data) || []).map((x: any) => ({ what: x.kind, label: x.label, at: x.created_at })) };
}

async function resumeLastWork() {
  const r = await sb().from('activity_log').select('kind,link_id,document_id,label,position')
    .in('kind', ['open_client', 'open_document', 'request_documents']).order('created_at', { ascending: false }).limit(1);
  const last = r && r.data && r.data[0];
  if (!last) return { ok: false, error: 'There is nothing to resume yet.' };
  if (last.link_id && side() === 'firm') {
    const tab = (last.position && last.position.tab) || (last.kind === 'request_documents' ? 'missing' : 'inbox');
    if (last.kind === 'open_document' && last.document_id) {
      const id = 'pg-' + Math.random().toString(36).slice(2, 10);
      pageGo.set(id, () => { g.fwNav?.('client', last.link_id, 'inbox'); g.faOpenDocument?.(last.document_id); });
      trace.page = { id, label: `${last.position?.client ?? 'Client'} › Inbox › ${last.label}` };
    } else {
      offerPage(`${last.label} › ${TAB_LABEL[tab] ?? 'Inbox'}`, () => g.fwNav?.('client', last.link_id, tab));
    }
  }
  return { ok: true, resumed: last.kind, label: last.label };
}

const TOOLS: Record<string, (a: any) => unknown> = {
  open_page: openPage, ledger_summary: ledgerSummary, find_entries: findEntries, pending_requests: pendingRequests,
  list_clients: async (a: { filter?: string }) => {
    const o = await overview();
    if (!o || !o.ok) return { ok: false, error: 'Clients could not be loaded.' };
    const f = String(a.filter ?? 'all');
    const rows = o.clients.filter((c: any) => f === 'all' || (f === 'mine' ? c.mine : c.status === f)).slice(0, 25);
    return { ok: true, summary: o.summary, clients: rows.map((c: any) => ({ client: c.client_name, status: c.status,
      documents: c.docs_requested ? `${c.docs_received}/${c.docs_requested}` : 'none requested', to_review: c.to_review,
      assigned: c.assigned.map((x: any) => x.name) })) };
  },
  open_client: openClient, client_missing: clientMissing, open_document: openDocument, get_row: getRow,
  request_documents: requestDocuments, link_client_code: linkClientCode,
  recent_activity: recentActivity, resume_last_work: resumeLastWork,
};

async function context(s: Side): Promise<string> {
  if (s === 'customer') return typeof g.aiGetContext === 'function' ? String(g.aiGetContext()) : '';
  const o = await overview();
  const role = g.navFirmContext?.()?.role ?? '';
  return o && o.ok ? `Firm: ${o.firm_name}. Your role: ${role}. Clients: ${o.summary.total}; waiting for documents: ${o.summary.waiting}; documents to review: ${o.summary.inbox}; missing documents: ${o.summary.missing}.` : '';
}

async function agentChat(history: Array<{ role: string; content: string }>, voice: boolean): Promise<ChatShape | null> {
  const s = side();
  trace = { tools: [], choices: [], page: null };
  const msgs: AgentMsg[] = history.filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({ role: m.role, content: String(m.content ?? '').slice(0, 2000) }));
  const ctx = await context(s);
  for (let round = 0; round < 6; round++) {
    let res: Response;
    try { res = await legacy.postApi('/api/agent', { side: s, voice, context: ctx, messages: msgs }); }
    catch { return null; }
    const d = await res.json().catch(() => ({})) as { message?: { content?: string; tool_calls?: ToolCall[] }; error?: string; code?: string };
    if (!res.ok) {
      if (d.code === 'AGENT_OFF' || res.status >= 500) return null;
      return { choices: [{ message: { content: d.error ?? 'The assistant is not available right now.' } }] };
    }
    const calls = d.message?.tool_calls ?? [];
    if (!calls.length) return { choices: [{ message: { content: d.message?.content || 'Done.' } }], _agent: trace };
    msgs.push({ role: 'assistant', content: d.message?.content ?? '', tool_calls: calls });
    for (const c of calls) {
      let result: unknown;
      trace.tools.push(toolLabel(c));
      try {
        const tool = TOOLS[c.function.name];
        result = tool ? await tool(JSON.parse(c.function.arguments || '{}')) : { ok: false, error: 'Unknown tool.' };
      } catch { result = { ok: false, error: 'The action failed.' }; }
      msgs.push({ role: 'tool', tool_call_id: c.id, content: JSON.stringify(result).slice(0, 3500) });
    }
  }
  return { choices: [{ message: { content: 'I stopped after several steps. Tell me what you would like to do next.' } }], _agent: trace };
}

/** "open_client("Ahmed")" — yalnız ilk argüman, kısaltılmış. */
function toolLabel(c: ToolCall): string {
  let first = '';
  try {
    const a = JSON.parse(c.function.arguments || '{}') ?? {};
    /* En belirleyici argüman: belge > sayfa > müşteri (katalog: open_document("subat.pdf")). */
    const key = ['document', 'page', 'query', 'client', 'label', 'filter', 'period'].find((k) => typeof a[k] === 'string' || typeof a[k] === 'number');
    const v = key ? a[key] : undefined;
    if (typeof v === 'string') first = JSON.stringify(v.slice(0, 40));
    else if (typeof v === 'number') first = String(v);
  } catch { /* argümansız göster */ }
  return `${c.function.name}(${first})`;
}

/** Cevap baloncuğunu izle süsler: araç satırı üstte, seçimler ve sayfa düğmesi altta. */
function decorate(t: Trace | undefined, html: string): string {
  if (!t) return html;
  const tools = t.tools.length ? `<div class="ag-tools">⚙ ${esc(t.tools.join(' · '))}</div>` : '';
  const choices = t.choices.length
    ? `<div class="ag-choices">${t.choices.map((c) => `<button class="btn bg bsm ag-choice" data-q="${esc(c.value)}">${esc(c.label)}</button>`).join('')}</div>` : '';
  const page = t.page ? `<button class="ag-page" data-go="${esc(t.page.id)}">Open: ${esc(t.page.label)} ›</button>` : '';
  return tools + `<div>${html}</div>` + choices + page;
}

export default defineFeature({
  id: 'orb-agent',
  label: 'Orb ajanı',
  view: null,
  boot: () => {
    /* Seçim ve sayfa düğmeleri için tek dinleyici: veri data-* içinde, onclick'e kullanıcı metni girmez. */
    document.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement | null)?.closest?.('.ag-choice, .ag-page') as HTMLElement | null;
      if (!b) return;
      if (b.classList.contains('ag-choice')) { b.parentElement?.querySelectorAll('button').forEach((x) => { (x as HTMLButtonElement).disabled = true; }); g.aiQuick?.(b.dataset.q ?? ''); }
      else pageGo.get(b.dataset.go ?? '')?.();
    });
  },
  legacyGlobals: {
    agentDecorate: (t: unknown, html: unknown) => decorate(t as Trace | undefined, String(html ?? '')),
    agentGo: (id: unknown) => pageGo.get(String(id))?.(),
    agentResume: async () => {
      trace = { tools: [], choices: [], page: null };
      const r = await resumeLastWork() as { ok: boolean; error?: string };
      if (!r.ok) { legacy.toast(r.error ?? 'There is nothing to resume yet.'); return; }
      if (trace.page) pageGo.get(trace.page.id)?.();
    },
    agentChat: (history: unknown, voice: unknown) => agentChat(Array.isArray(history) ? history : [], voice === true),
    agentConfirm: async (id: unknown) => {
      const run = pending.get(String(id));
      const box = document.getElementById(String(id));
      const actions = box?.querySelector('[data-actions]') as HTMLElement | null;
      if (!run || !actions) return;
      pending.delete(String(id));
      actions.innerHTML = '<span style="font-size:11px;color:var(--t2)">…</span>';
      actions.innerHTML = `<span style="font-size:12px">${await run()}</span>`;
    },
    agentCancel: (id: unknown) => {
      pending.delete(String(id));
      const actions = document.getElementById(String(id))?.querySelector('[data-actions]') as HTMLElement | null;
      if (actions) actions.innerHTML = '<span style="font-size:11px;color:var(--t2)">Cancelled.</span>';
    },
  },
});
